import { CUSTOM_INSTANCE_GRANT } from "@colossus/contracts";
import type { GuildBadge } from "@colossus/contracts";
import {
  SERVERS_PATH,
  landingPath,
  released,
  tenantEntryPath,
} from "@colossus/contracts/tenant-home";
import { m } from "./paraglide/messages.js";
import type { Message } from "./locale.ts";

export interface DashboardTenant {
  readonly tenantId: string;
  readonly botName: string;
  readonly sharedBot: boolean;
  readonly botAppId: string | null;
  readonly guildId: string | null;
  readonly guildName: string | null;
  readonly guildIconUrl: string | null;
  readonly guildBadge: GuildBadge | null;
  readonly status: string;
  readonly owner: boolean;
  readonly awaitingCheckout: boolean;
}

export const awaitingCheckout = (tenant: {
  readonly sharedBot: boolean;
  readonly botAppId: string | null;
  readonly status: string;
  readonly grants: readonly string[];
  readonly awaitingSharedBot?: boolean;
}): boolean =>
  tenant.awaitingSharedBot !== true &&
  !tenant.sharedBot &&
  tenant.botAppId !== null &&
  !released(tenant.status) &&
  !tenant.grants.includes(CUSTOM_INSTANCE_GRANT);

export interface TenantPill {
  readonly label: Message;
  readonly status?: "good" | "warning" | "serious" | "critical";
}

const STATUS_PILL: Record<string, TenantPill> = {
  running: { label: m.dashboard_status_live, status: "good" },
  provisioning: { label: m.dashboard_status_provisioning },
  failed: { label: m.dashboard_status_failed, status: "critical" },
  compromised: { label: m.dashboard_status_compromised, status: "critical" },
  suspended: { label: m.dashboard_status_suspended, status: "serious" },
  deprovisioning: { label: m.dashboard_status_deprovisioning, status: "serious" },
  deleted: { label: m.dashboard_status_deleted },
};

export const tenantPill = (tenant: DashboardTenant): TenantPill => {
  if (tenant.guildId === null) return { label: m.dashboard_status_pending };
  if (tenant.awaitingCheckout) return { label: m.dashboard_status_awaiting_checkout };
  const pill = STATUS_PILL[tenant.status];
  if (pill === undefined) return { label: m.dashboard_status_unknown, status: "warning" };
  return pill;
};

export const switchableTenants = (
  tenants: readonly DashboardTenant[],
  tenantId: string | null,
): readonly DashboardTenant[] =>
  tenants.filter((tenant) => tenant.status !== "deleted" || tenant.tenantId === tenantId);

export const tenantHref = (tenant: DashboardTenant): string => tenantEntryPath(tenant);

export const landingHref = (
  workspace: { readonly tenants: readonly DashboardTenant[]; readonly failure: unknown | null },
  lastTenantId: string | null,
): string =>
  workspace.failure === null ? landingPath(workspace.tenants, lastTenantId) : SERVERS_PATH;

export const tenantLabel = (tenant: Pick<DashboardTenant, "guildName" | "botName">): string =>
  tenant.guildName ?? tenant.botName;

export const soleOwnedTenant = (tenants: readonly DashboardTenant[]): DashboardTenant | null => {
  const owned = tenants.filter((tenant) => tenant.owner && tenant.guildId !== null);
  const [only] = owned;
  return owned.length === 1 && only !== undefined ? only : null;
};
